import { IconButtonProps } from 'bento';
import { Theme, css } from '@emotion/react';

type ColorTheme = NonNullable<IconButtonProps['color']>;

const getColorThemeVars = (colors: Theme['colors'], color: ColorTheme) => {
  switch (color) {
    case 'primary':
      return css`
        --contained-background-color: ${colors.bgPrimaryBase};
        --contained-icon-color: ${colors.fgContrast};
        --contained-hover-color: ${colors.bgPrimaryDark};
        --contained-disabled-background-color: ${colors.bgLight};

        --outlined-border-color: ${colors.borderPrimaryBase};
        --outlined-icon-color: ${colors.fgPrimaryBase};
        --outlined-hover-color: ${colors.bgPrimaryLighter};
        --outlined-disabled-border-color: ${colors.borderDefault};
        --outlined-disabled-icon-color: ${colors.fgDisabled};

        --text-icon-color: ${colors.fgPrimaryBase};
        --text-hover-color: ${colors.bgPrimaryLighter};
        --text-disabled-icon-color: ${colors.fgDisabled};

        --button-outline-color: ${colors.borderPrimaryBase};
      `;
    case 'subtle':
      return css`
        --contained-background-color: ${colors.bgDark};
        --contained-icon-color: ${colors.fgContrast};
        --contained-hover-color: ${colors.bgDarker};
        --contained-disabled-background-color: ${colors.bgLight};

        --outlined-border-color: ${colors.borderDefault};
        --outlined-icon-color: ${colors.fgDefault};
        --outlined-hover-color: ${colors.bgLighter};
        --outlined-disabled-border-color: ${colors.borderDefault};
        --outlined-disabled-icon-color: ${colors.fgDisabled};

        --text-icon-color: ${colors.fgDefault};
        --text-hover-color: ${colors.bgLighter};
        --text-disabled-icon-color: ${colors.fgDisabled};

        --button-outline-color: ${colors.borderPrimaryBase};
      `;
    case 'danger':
      return css`
        --contained-background-color: ${colors.bgDangerBase};
        --contained-icon-color: ${colors.fgContrast};
        --contained-hover-color: ${colors.bgDangerDark};
        --contained-disabled-background-color: ${colors.bgLight};

        --outlined-border-color: ${colors.borderDangerBase};
        --outlined-icon-color: ${colors.fgDangerBase};
        --outlined-hover-color: ${colors.bgDangerLighter};
        --outlined-disabled-border-color: ${colors.borderDefault};
        --outlined-disabled-icon-color: ${colors.fgDisabled};

        --text-icon-color: ${colors.fgDangerBase};
        --text-hover-color: ${colors.bgDangerLighter};
        --text-disabled-icon-color: ${colors.fgDisabled};

        --button-outline-color: ${colors.borderPrimaryBase};
      `;
    case 'contrast':
      return css`
        --contained-background-color: ${colors.bgDefault};
        --contained-icon-color: ${colors.fgDefault};
        --contained-hover-color: ${colors.bgLighter};
        --contained-disabled-background-color: ${colors.bgDark};

        --outlined-border-color: ${colors.borderContrast};
        --outlined-icon-color: ${colors.fgContrast};
        --outlined-hover-color: ${colors.bgDarker};
        --outlined-disabled-border-color: ${colors.borderDefault};
        --outlined-disabled-icon-color: ${colors.fgDisabled};

        --text-icon-color: ${colors.fgContrast};
        --text-hover-color: ${colors.bgDarker};
        --text-disabled-icon-color: ${colors.fgDisabled};

        --button-outline-color: ${colors.bgDefault};
      `;
    case 'internal':
      return css`
        --contained-background-color: ${colors.bgInternalBase};
        --contained-icon-color: ${colors.fgContrast};
        --contained-hover-color: ${colors.bgInternalDark};
        --contained-disabled-background-color: ${colors.bgLight};

        --outlined-border-color: ${colors.borderInternalBase};
        --outlined-icon-color: ${colors.fgInternalBase};
        --outlined-hover-color: ${colors.bgInternalLighter};
        --outlined-disabled-border-color: ${colors.borderDefault};
        --outlined-disabled-icon-color: ${colors.fgDisabled};

        --text-icon-color: ${colors.fgInternalBase};
        --text-hover-color: ${colors.bgInternalLighter};
        --text-disabled-icon-color: ${colors.fgDisabled};

        --button-outline-color: ${colors.borderPrimaryBase};
      `;
  }
};

export const iconButtonSize = (
  { spacing, typography }: Theme,
  size: NonNullable<IconButtonProps['size']>
) => {
  switch (size) {
    case 'small':
      return css`
        --button-padding: ${spacing(0.75)}px;

        svg {
          font-size: ${typography.pxToRem(20)};
        }
      `;

    case 'medium':
      return css`
        --button-padding: ${spacing(1)}px;

        svg {
          font-size: ${typography.pxToRem(20)};
        }
      `;

    case 'large':
      return css`
        --button-padding: ${spacing(1.5)}px;

        svg {
          font-size: ${typography.pxToRem(20)};
        }
      `;
  }
};

export const iconButtonBase = (
  { colors, borderRadius, interactiveStates }: Theme,
  color: ColorTheme
) => {
  return css`
    ${getColorThemeVars(colors, color)}

    border-radius: ${borderRadius[100]};
    padding: var(--button-padding);

    ${interactiveStates.focus(css`
      box-shadow: none;
      outline: 2px solid var(--button-outline-color);
      outline-offset: 2px;
    `)}

    .MuiTouchRipple-child {
      border-radius: ${borderRadius[100]};
    }
  `;
};

export const iconButtonVariant = (
  { interactiveStates }: Theme,
  variant: NonNullable<IconButtonProps['variant']>
) => {
  switch (variant) {
    case 'contained':
      return css`
        // The !important part here works around a bug in how upstream MUI components are styled. There is a style that is
        // applied to the button on :hover which specifies the background should be a dark/default with a similar @media
        // specifier.
        // Or light in light mode.
        // This is intended to compensate for cases where a component can get a "stuck-on" :hover state when the user tapped,
        // it is a peculiarity of iOS browsers. But that's not what we want for a contained button! We want background color.
        // !important is sadly the easiest way to make sure this doesn't get applied.

        background-color: var(--contained-background-color) !important;
        color: var(--contained-icon-color);

        // And also with browsers that actually support hover, we do want to have the button hover with a different color.
        // Since we used !important to fix that above, we need to reuse it here.
        //
        // Note: interActiveStates.hover() takes care of applying the correct @media selector here too.
        ${interactiveStates.hover(css`
          background-color: var(--contained-hover-color) !important;
        `)}

        ${interactiveStates.active(css`
          background-color: var(--contained-hover-color) !important;
        `)}

        ${interactiveStates.disabled(css`
          background-color: var(--contained-disabled-background-color) !important;
          color: var(--contained-icon-color);
        `)}
      `;

    case 'outlined':
      return css`
        border: 1px solid var(--outlined-border-color);
        color: var(--outlined-icon-color);
        padding: calc(var(--button-padding) - 1px);

        ${interactiveStates.hover(css`
          background-color: var(--outlined-hover-color);
        `)}

        ${interactiveStates.active(css`
          background-color: var(--outlined-hover-color);
        `)}

        ${interactiveStates.disabled(css`
          border-color: var(--outlined-disabled-border-color);
          color: var(--outlined-disabled-icon-color);
        `)}
      `;

    case 'text':
      return css`
        color: var(--text-icon-color);

        ${interactiveStates.hover(css`
          background-color: var(--text-hover-color);
        `)}

        ${interactiveStates.active(css`
          background-color: var(--text-hover-color);
        `)}

        ${interactiveStates.disabled(css`
          color: var(--text-disabled-icon-color);
        `)}
      `;
  }
};
